import * as React from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0Button, F0Card } from '@factorialco/f0-react'
import { Input, OneAlert, OneModal, Textarea } from '@factorialco/f0-react/dist/experimental'
import { Info, Money, Plane } from '@factorialco/f0-react/icons/app'
import {
  CreateItManagementOrderDocument,
  type DistributorProductVariantsQuery,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { buildUrl } from 'modules/core/lib/buildUrl'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import emptyStateImage from 'modules/it_management/pages/Empty-state-device-catalog-card.png'

type DistributorProductVariant =
  DistributorProductVariantsQuery['itManagement']['distributorProductVariantsConnection']['nodes'][number]

interface RequestDeviceModalProps {
  variant: DistributorProductVariant | null
  onClose: () => void
  onSuccess?: () => void
}

const formatPrice = (
  cost: number | string | null | undefined,
  period: number | null | undefined
): string => {
  if (!cost) return '-'
  const costNumber = typeof cost === 'string' ? parseFloat(cost) : cost
  if (isNaN(costNumber)) return '-'
  if (period && period > 0) {
    return `From ${costNumber.toFixed(2)} € /month`
  }
  return `${costNumber.toFixed(2)} €`
}

const RequestDeviceModal: React.FC<RequestDeviceModalProps> = ({ variant, onClose, onSuccess }) => {
  const { email, company, employee } = useCurrentSession()
  const [requestEmail, setRequestEmail] = React.useState(email || '')
  const [notes, setNotes] = React.useState('')

  const [createOrder] = useMutation(CreateItManagementOrderDocument, {
    extractorFn: (data) => data.itManagementMutations.createItmanagementOrder,
  })

  React.useEffect(() => {
    if (!variant) return
    const style = document.createElement('style')
    style.textContent = `
      [data-radix-portal] > div:has([role="dialog"]) {
        max-width: 820px !important;
        width: 820px !important;
      }
    `
    document.head.appendChild(style)
    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style)
      }
    }
  }, [variant])

  if (!variant) return null

  const deviceName =
    variant.product?.brand && variant.product?.name
      ? `${variant.product.brand} ${variant.product.name}`
      : variant.sku || variant.id

  const description = variant.description || variant.product?.description || '-'
  const price = formatPrice(variant.firstOfferCost, variant.firstOfferPeriod)
  const deliveryInfo =
    variant.deliveryConditions && variant.deliveryConditions !== '-'
      ? variant.deliveryConditions
      : i18n.t('it_management.device_catalog.standard_delivery')
  const deviceImage = variant.firstProductMediaImageUrl
    ? buildUrl(variant.firstProductMediaImageUrl)
    : emptyStateImage

  const handleSubmit = async () => {
    if (!company || !variant || !employee) return

    app.setProgress({
      message: i18n.t('it_management.device_catalog.request_modal.creating_order'),
    })

    const result = await createOrder({
      variables: {
        companyId: company.id,
        variationId: variant.id,
        distributorOfferId: undefined,
        employeeId: employee.id,
        quantity: 1,
        email: requestEmail,
        notes: notes.trim() || '-',
      },
    })

    app.setProgress(null)

    const handled = handleResult(result, ({ message }) => {
      app.setFlash({
        type: 'wrong',
        message:
          message || i18n.t('it_management.device_catalog.request_modal.failed_to_create_order'),
      })
    })

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('it_management.device_catalog.request_modal.order_created_successfully'),
      })
      onSuccess?.()
      onClose()
    }
  }

  return (
    <OneModal isOpen onClose={onClose} width="lg">
      <OneModal.Header title={i18n.t('it_management.device_catalog.request_modal.title')} />
      <Box height="s1" background="grey300" />
      <OneModal.Content withPadding>
        <Box flexDirection="row" gap="s24" width="full" paddingTop="s16" paddingBottom="s16">
          <Box style={{ width: '288px' }}>
            <F0Card
              image={deviceImage}
              title={deviceName}
              description={description}
              imageFit="contain"
              imageSize="lg"
              blurredBackground={false}
            >
              <Box gap="s8" flexDirection="column">
                {price !== '-' && (
                  <Box gap="s4" flexDirection="row" alignItems="center">
                    <Money width={14} height={14} />
                    <Text size="200" weight="normal">
                      {price}
                    </Text>
                  </Box>
                )}
                <Box gap="s4" flexDirection="row" alignItems="center">
                  <Plane width={14} height={14} />
                  <Text size="200" weight="normal">
                    {deliveryInfo}
                  </Text>
                </Box>
              </Box>
            </F0Card>
          </Box>

          <Box width="s1" background="grey300" />

          <Box gap="s16" flexDirection="column" style={{ width: '440px' }}>
            <Input
              required
              value={requestEmail}
              label={i18n.t('it_management.device_catalog.request_modal.email_label')}
              onChange={setRequestEmail}
              type="email"
              size="md"
            />
            <Box gap="s4" flexDirection="column">
              <Textarea
                value={notes}
                label={i18n.t('it_management.device_catalog.request_modal.notes_label')}
                onChange={setNotes}
              />
              <Box style={{ fontSize: '16px' }}>
                <Text size="100" weight="normal" color="grey700">
                  {i18n.t('it_management.device_catalog.request_modal.notes_placeholder')}
                </Text>
              </Box>
            </Box>
            <Box
              style={{
                marginTop: '160px',
                backgroundColor: 'var(--color-blue50)',
                border: 'none',
                borderRadius: '8px',
                padding: '12px',
              }}
            >
              <OneAlert
                variant="info"
                title={i18n.t('it_management.device_catalog.request_modal.info_title')}
                description={i18n.t('it_management.device_catalog.request_modal.info_description')}
                icon={Info}
              />
            </Box>
          </Box>
        </Box>
      </OneModal.Content>
      <OneModal.Footer>
        <Box gap="s8" flexDirection="row" justifyContent="flexEnd" width="full">
          <F0Button variant="outline" onClick={onClose} label={i18n.t('common.cancel')} />
          <F0Button
            onClick={handleSubmit}
            label={i18n.t('it_management.device_catalog.request_modal.send_button')}
            disabled={!requestEmail}
          />
        </Box>
      </OneModal.Footer>
    </OneModal>
  )
}

export default RequestDeviceModal
